import type { HttpClient } from "@dl/web-core-js/api";
import type { Currency, Period, SigmClientDTO } from "./reporting";

/** One invoice line — a SKU shipped on this invoice. */
export interface InvoiceLineDTO {
  id: number;
  invoice_no?: string | null;
  seq_no?: number | null;
  part?: { no?: string | null; description1?: string | null } | null;
  price?: number | null;
  ordered_qty?: number | null;
  ship_qty?: number | null;
  discount_percent?: number | null;
  commission_percent?: number | null;
  cost?: number | null;
  ship_date?: string | null;
  status?: string | null;
}

/** `getLineTotal()`: price × shipped quantity, before the line discount. */
export function invoiceLineTotal(line: InvoiceLineDTO): number {
  return (line.price ?? 0) * (line.ship_qty ?? 0);
}

/** `getLineNet()`: the line total after its own discount percentage. */
export function invoiceLineNet(line: InvoiceLineDTO): number {
  return invoiceLineTotal(line) * (1 - (line.discount_percent ?? 0) / 100);
}

/** `getLineCommission()`: the salesperson's cut of the line's net. */
export function invoiceLineCommission(line: InvoiceLineDTO): number {
  return ((line.commission_percent ?? 0) / 100) * invoiceLineNet(line);
}

/**
 * An ERP invoice header, with lines when the detail endpoint is used.
 *
 * `order_type` is numeric on the wire — 0 other, 1 order, 2 refund, matching
 * SIGM's `ORD_TYPE` and the Java `OrderType` enum — mapped to labels at the
 * edge rather than shipped as magic numbers through the UI.
 */
export interface InvoiceDTO {
  id: number;
  no?: string | null;
  date?: string | null;
  status?: string | null;
  order_type?: number | null;
  order_status?: string | null;
  currency?: Currency | null;
  warehouse?: string | null;
  client?: SigmClientDTO | null;
  order_no?: string | null;
  client_order_no?: string | null;
  salesperson_name?: string | null;
  carrier?: string | null;
  subtotal?: number | null;
  shipping_amount?: number | null;
  discount_amount?: number | null;
  tax1_amount?: number | null;
  tax2_amount?: number | null;
  ship_name1?: string | null;
  ship_city?: string | null;
  bill_name1?: string | null;
  bill_city?: string | null;
  note1?: string | null;
  note2?: string | null;
  note3?: string | null;
  note4?: string | null;
  bill_name2?: string | null;
  bill_address?: string | null;
  bill_postal_code?: string | null;
  bill_phone?: string | null;
  ship_name2?: string | null;
  ship_address?: string | null;
  ship_postal_code?: string | null;
  ship_phone?: string | null;
  ship_email?: string | null;
  packing_slip_no?: string | null;
  client_tax_group_no?: string | null;
  /**
   * Only on a confirmed slip still waiting for its number: STUCK when the last
   * invoice generation run skipped it, TO_INVOICE when the next run should take it.
   */
  pending_state?: "STUCK" | "TO_INVOICE" | null;
  /** Days since the slip was confirmed, on the same rows. */
  days_waiting?: number | null;
  lines?: InvoiceLineDTO[];
}

/** `getTaxTotal()`: the two SIGM tax buckets summed. */
export function invoiceTaxTotal(invoice: InvoiceDTO): number {
  return (invoice.tax1_amount ?? 0) + (invoice.tax2_amount ?? 0);
}

/**
 * `getLineTax1()` / `getLineTax2()`: the invoice-level tax buckets prorated
 * onto a line by its share of the subtotal — SIGM stores tax on the header
 * only, so a per-line figure has to be derived.
 */
export function invoiceLineTax(
  invoice: InvoiceDTO,
  line: InvoiceLineDTO,
  bucket: 1 | 2,
): number {
  const amount = (bucket === 1 ? invoice.tax1_amount : invoice.tax2_amount) ?? 0;
  const subtotal = invoice.subtotal ?? 0;
  if (subtotal === 0 || amount === 0) return 0;
  return amount * (invoiceLineNet(line) / subtotal);
}

export function invoiceOrderTypeLabel(type: number | null | undefined): "ORDER" | "OTHER" | "REFUND" | "" {
  if (type === 1) return "ORDER";
  if (type === 2) return "REFUND";
  if (type === 0) return "OTHER";
  return "";
}

export function invoiceGrandTotal(invoice: InvoiceDTO): number {
  return (
    (invoice.subtotal ?? 0) +
    (invoice.shipping_amount ?? 0) -
    (invoice.discount_amount ?? 0) +
    (invoice.tax1_amount ?? 0) +
    (invoice.tax2_amount ?? 0)
  );
}

export class InvoiceClient {
  constructor(private readonly http: HttpClient) {}

  /** Headers only — the master grid's load. */
  byPeriod(period: Period): Promise<InvoiceDTO[]> {
    return this.http.get<InvoiceDTO[]>("/reporting/invoice/period", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  /**
   * Confirmed packing slips with no invoice number yet, oldest first — the ones
   * a generation run skipped (STUCK) and the ones the next run should take.
   */
  awaiting(): Promise<InvoiceDTO[]> {
    return this.http.get<InvoiceDTO[]>("/reporting/invoice/awaiting");
  }

  /** One invoice with its lines — the detail view. */
  detail(invoiceId: number): Promise<InvoiceDTO> {
    return this.http.get<InvoiceDTO>(`/reporting/invoice/detail/${invoiceId}`);
  }

  /** Invoices containing one SKU, lines included — the product tab's load. */
  byPeriodAndPart(partNo: string, period: Period): Promise<InvoiceDTO[]> {
    return this.http.get<InvoiceDTO[]>(
      `/reporting/invoice/period/lines/part/${encodeURIComponent(partNo)}`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }
}
